import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, Upload, ArrowLeft, Save } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

// Convert a File to a compressed data URL for storage in Mongo.
async function fileToDataUrl(file, maxDim = 1600, quality = 0.82) {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", quality);
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState(null); // slug
  const [current, setCurrent] = useState(null); // full condition
  const [form, setForm] = useState({ name: "", description: "", symptoms: "", advances: "" });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.listConditions().then(setItems).catch((e) => toast.error(e.message));
  }, []);

  useEffect(() => {
    if (!selected) {
      setCurrent(null);
      return;
    }
    api
      .getCondition(selected)
      .then((c) => {
        setCurrent(c);
        setForm({ name: c.name, description: c.description, symptoms: c.symptoms, advances: c.advances });
      })
      .catch((e) => toast.error(e.message));
  }, [selected]);

  const doLogout = async () => {
    await logout();
    nav("/admin/login", { replace: true });
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await api.updateCondition(selected, form);
      setCurrent(updated);
      setItems((prev) => prev.map((x) => (x.slug === updated.slug ? updated : x)));
      toast.success("Saved.");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      let updated = current;
      for (const f of files) {
        const dataUrl = await fileToDataUrl(f);
        updated = await api.addConditionImage(selected, dataUrl, "");
      }
      setCurrent(updated);
      setItems((prev) => prev.map((x) => (x.slug === updated.slug ? updated : x)));
      toast.success(`${files.length} image${files.length === 1 ? "" : "s"} uploaded.`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeImage = async (imgId) => {
    if (!window.confirm("Delete this image? This cannot be undone.")) return;
    try {
      const updated = await api.deleteConditionImage(selected, imgId);
      setCurrent(updated);
      setItems((prev) => prev.map((x) => (x.slug === updated.slug ? updated : x)));
      toast.success("Image removed.");
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="pt-36 pb-16 px-6 lg:px-12 min-h-[100svh]" data-testid="admin-dashboard">
      <div className="mx-auto max-w-[1600px]">
        <div className="flex items-baseline justify-between border-b border-navy-600/15 pb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-brass-500">Admin console</div>
            <h1
              className="mt-2 tracking-[-0.02em] text-navy-600 leading-[0.95] text-[clamp(2rem,4.5vw,3.75rem)]"
              style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', serif", fontWeight: 500 }}
            >
              Manage conditions
            </h1>
            <p className="mt-2 text-[10px] uppercase tracking-[0.24em] text-navy-600/60">
              Signed in as {user?.email}
            </p>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/conditions" className="link-underline text-[11px] uppercase tracking-[0.24em]" data-testid="admin-view-public">
              View public site
            </Link>
            <button
              onClick={doLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-navy-600/80 hover:text-navy-600"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-8 mt-10">
          {/* List */}
          <aside className="col-span-12 md:col-span-4 lg:col-span-3">
            <div className="text-[10px] uppercase tracking-[0.28em] text-navy-600/60 mb-4">
              {items?.length || 0} conditions
            </div>
            <ul className="max-h-[70vh] overflow-y-auto pr-2 divide-y divide-navy-600/10 border border-navy-600/15">
              {(items || []).map((c) => (
                <li key={c.slug}>
                  <button
                    onClick={() => setSelected(c.slug)}
                    data-testid={`admin-select-${c.slug}`}
                    className={`w-full text-left px-4 py-4 hover:bg-beige-200 transition-colors ${
                      selected === c.slug ? "bg-navy-600 text-beige-100 hover:bg-navy-700" : ""
                    }`}
                  >
                    <div className="font-serif text-lg tracking-tight">{c.name}</div>
                    <div className={`mt-1 text-[10px] uppercase tracking-[0.24em] ${selected === c.slug ? "text-beige-100/60" : "text-navy-600/50"}`}>
                      {c.images?.length || 0} image{(c.images?.length || 0) === 1 ? "" : "s"}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          {/* Editor */}
          <section className="col-span-12 md:col-span-8 lg:col-span-9" data-testid="admin-editor">
            {!current ? (
              <div className="border border-dashed border-navy-600/25 py-32 text-center text-[10px] uppercase tracking-[0.28em] text-navy-600/50">
                <ArrowLeft size={18} className="mx-auto mb-4 text-navy-600/40" />
                Select a condition on the left to edit
              </div>
            ) : (
              <>
                <div className="flex items-baseline justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.28em] text-brass-500">
                      Editing / {current.slug}
                    </div>
                    <h2
                      className="mt-2 tracking-[-0.02em] text-navy-600 leading-[1] text-[clamp(2rem,4vw,3.25rem)]"
                      style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', serif", fontWeight: 500 }}
                    >
                      {current.name}
                    </h2>
                  </div>
                  <button
                    onClick={save}
                    disabled={saving}
                    data-testid="admin-save"
                    className="inline-flex items-center gap-2 bg-navy-600 text-beige-100 px-6 py-3 text-[11px] uppercase tracking-[0.24em] hover:bg-navy-700 disabled:opacity-60"
                  >
                    <Save size={14} /> {saving ? "Saving…" : "Save changes"}
                  </button>
                </div>

                <div className="mt-10 grid grid-cols-1 gap-8">
                  <Field label="Display name">
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      className="luxe-input w-full py-3 text-navy-600"
                      data-testid="admin-field-name"
                    />
                  </Field>

                  <Field label="What it is (8–9 lines)">
                    <textarea
                      rows={5}
                      value={form.description}
                      onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                      className="luxe-input w-full py-3 text-navy-600 resize-y"
                      data-testid="admin-field-description"
                    />
                  </Field>

                  <Field label="Symptoms">
                    <textarea
                      rows={4}
                      value={form.symptoms}
                      onChange={(e) => setForm((f) => ({ ...f, symptoms: e.target.value }))}
                      className="luxe-input w-full py-3 text-navy-600 resize-y"
                      data-testid="admin-field-symptoms"
                    />
                  </Field>

                  <Field label="Recent advances in management">
                    <textarea
                      rows={4}
                      value={form.advances}
                      onChange={(e) => setForm((f) => ({ ...f, advances: e.target.value }))}
                      className="luxe-input w-full py-3 text-navy-600 resize-y"
                      data-testid="admin-field-advances"
                    />
                  </Field>
                </div>

                {/* Images */}
                <div className="mt-16 border-t border-navy-600/15 pt-8">
                  <div className="flex items-baseline justify-between">
                    <h3 className="font-serif text-2xl text-navy-600">Case images</h3>
                    <label
                      className="inline-flex items-center gap-2 border border-navy-600 text-navy-600 px-5 py-3 text-[11px] uppercase tracking-[0.24em] hover:bg-navy-600 hover:text-beige-100 cursor-pointer transition-colors"
                      data-testid="admin-upload-label"
                    >
                      <Upload size={14} />
                      {uploading ? "Uploading…" : "Upload images"}
                      <input
                        ref={fileRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={onUpload}
                        className="hidden"
                        data-testid="admin-upload-input"
                      />
                    </label>
                  </div>

                  {current.images.length === 0 ? (
                    <div className="mt-6 border border-dashed border-navy-600/25 py-16 text-center text-[10px] uppercase tracking-[0.28em] text-navy-600/50">
                      No images yet — click Upload to add
                    </div>
                  ) : (
                    <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {current.images.map((img) => (
                        <figure key={img.id} className="relative aspect-square overflow-hidden group">
                          <img src={img.data_url} alt={img.caption || current.name} className="w-full h-full object-cover" />
                          <button
                            onClick={() => removeImage(img.id)}
                            data-testid={`admin-delete-image-${img.id}`}
                            className="absolute top-2 right-2 bg-navy-600/80 backdrop-blur text-beige-100 p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 size={14} />
                          </button>
                        </figure>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.28em] text-navy-600/60 mb-3">{label}</div>
      {children}
    </div>
  );
}
